
export interface HeavyWeatherLimitElement {
  name: keyof HeavyWeatherLimits;
  text: string;
  unit?: string;
  min?: number;
  max?: number;
  connectedTo?: keyof HeavyWeatherLimits;
  connectedToText?: string;
  dropdown?: { value: number; text: string; }[];
}

export interface CustomVessel {
    name: string,
    imo:  number,
    maerskPolicy: CustomHeavyWeatherLimits | {},
    customLimits: CustomHeavyWeatherLimits | {},
}

export interface HeavyWeatherLimits {
  Waves: TEU;
  Wind10M: TEU;
  Wind50M: TEU;
  Visibility: TEU;
  Icing: TEU;
  Hurricanes: TEU;
  TropicalStorms: TEU;
  Depressions: TEU;
  WindGusts10M: TEU;
  WindGusts50M: TEU;
  RiskWindSpeed: TEU;
  RiskWaveHeight: TEU;
  HeadWind: TEU;
  BeamWind: TEU;
  FollowingWind: TEU;
  BeamWavesHeight: TEU;
  BeamWavesPeriod: TEUWithPeriod;
  HeadWavesHeight: TEU;
  HeadWavesPeriod: TEUWithPeriod;
  FollowingWavesHeight: TEU;
  FollowingWavesPeriod: TEUWithPeriod;
  IceConcentration: TEU;
}

export interface CustomHeavyWeatherLimits {
  Waves: number | null;
  Wind10M: number | null;
  Wind50M: number | null;
  Visibility: number | null;
  Icing: number | null;
  Hurricanes: number | null;
  TropicalStorms: number | null;
  Depressions: number | null;
  WindGusts10M: number | null;
  WindGusts50M: number | null;
  RiskWindSpeed: number | null;
  RiskWaveHeight: number | null;
  HeadWind: number | null;
  BeamWind: number | null;
  FollowingWind: number | null;
  BeamWavesHeight: number | null;
  BeamWavesPeriod: {min: number | null; max: number | null};
  HeadWavesHeight: number | null;
  HeadWavesPeriod: {min: number | null; max: number | null};
  FollowingWavesHeight: number | null;
  FollowingWavesPeriod: {max:number | null; min: number | null};
  IceConcentration: number | null;
}

export interface TEU {
  teuLessthan2500: number | null;
  teuBetween2500to5000: number | null;
  teuMorethan5000: number | null;
}

export interface TEUWithPeriod {
  teuLessthan2500: {
    max: number | null;
    min: number | null;
  };
  teuBetween2500to5000: {
    max: number | null;
    min: number | null;
  };
  teuMorethan5000: {
    max: number | null;
    min: number | null;
  };
}

export const wind50MMinValues: TEU = {
  teuLessthan2500: 17,
  teuBetween2500to5000: 17,
  teuMorethan5000: 20,
};

export const wavesMinValues: TEU = {
  teuLessthan2500: 4,
  teuBetween2500to5000: 5,
  teuMorethan5000: 6,
};

export const ELEMENT_DATA: HeavyWeatherLimitElement[] = [
  {
    name: "Wind10M",
    text: "Maximum wind speed 10m",
    unit: "(m/s)",
    max: 30,
  },
  {
    name: "Wind50M",
    text: "Maximum wind speed 50m",
    unit: "(m/s)",
    max: 30,
  },
  {
    name: "WindGusts10M",
    text: "Maximum wind gusts 10m",
    unit: "(m/s)",
    max: 30,
  },
  {
    name: "WindGusts50M",
    text: "Maximum wind gusts 50m",
    unit: "(m/s)",
    max: 30,
  },
  {
    name: "RiskWindSpeed",
    text: "Maximum risk wind speed",
    unit: "(m/s)",
    max: 30,
  },
  {
    name: "Waves",
    text: "Maximum wave height",
    unit: "(m)",
    max: 14,
  },
  {
    name: "RiskWaveHeight",
    text: "Maximum risk wave height",
    unit: "(m)",
    max: 14,
  },
  {
    name: "HeadWavesHeight",
    text: "Head waves, higher than",
    unit: "(m)",
    max: 14,
  },
  {
    name: "HeadWavesPeriod",
    text: "with periods",
    unit: "(sec)",
    connectedTo: "HeadWavesHeight",
    connectedToText: "Head waves, higher than (m)",
    max: 20,
  },
  {
    name: "BeamWavesHeight",
    text: "Beam waves, higher than",
    unit: "(m)",
    max: 14,
  },
  {
    name: "BeamWavesPeriod",
    text: "with periods",
    unit: "(sec)",
    connectedTo: "BeamWavesHeight",
    connectedToText: "Beam waves, higher than (m)",
    max: 20,
  },
  {
    name: "FollowingWavesHeight",
    text: "Following waves, higher than",
    unit: "(m)",
    max: 14,
  },
  {
    name: "FollowingWavesPeriod",
    text: "with periods",
    unit: "(sec)",
    connectedTo: "FollowingWavesHeight",
    connectedToText: "Following waves, higher than (m)",
    max: 20,
  },
  {
    name: "Visibility",
    text: "Minimum visibility",
    dropdown: getVisibilityValueTexts(),
    unit: ""
  },
  {
    name: "Icing",
    text: "Maximum risk of icing",
    dropdown: getIcingValueTexts(),
    unit: ""
  },
];

export const WEATHER_LIMIT_DATA: HeavyWeatherLimits = {
  Waves: {
    teuLessthan2500: null,
    teuBetween2500to5000: null,
    teuMorethan5000: null,
  },
  Wind10M: {
    teuLessthan2500: null,
    teuBetween2500to5000: null,
    teuMorethan5000: null,
  },
  Wind50M: {
    teuLessthan2500: null,
    teuBetween2500to5000: null,
    teuMorethan5000: null,
  },
  Visibility: {
    teuLessthan2500: null,
    teuBetween2500to5000: null,
    teuMorethan5000: null,
  },
  Icing: {
    teuLessthan2500: null,
    teuBetween2500to5000: null,
    teuMorethan5000: null,
  },
  Hurricanes: {
    teuLessthan2500: null,
    teuBetween2500to5000: null,
    teuMorethan5000: null,
  },
  TropicalStorms: {
    teuLessthan2500: null,
    teuBetween2500to5000: null,
    teuMorethan5000: null,
  },
  Depressions: {
    teuLessthan2500: null,
    teuBetween2500to5000: null,
    teuMorethan5000: null,
  },
  WindGusts10M: {
    teuLessthan2500: null,
    teuBetween2500to5000: null,
    teuMorethan5000: null,
  },
  WindGusts50M: {
    teuLessthan2500: null,
    teuBetween2500to5000: null,
    teuMorethan5000: null,
  },
  RiskWindSpeed: {
    teuLessthan2500: null,
    teuBetween2500to5000: null,
    teuMorethan5000: null,
  },
  RiskWaveHeight: {
    teuLessthan2500: null,
    teuBetween2500to5000: null,
    teuMorethan5000: null,
  },
  HeadWind: {
    teuLessthan2500: null,
    teuBetween2500to5000: null,
    teuMorethan5000: null,
  },
  BeamWind: {
    teuLessthan2500: null,
    teuBetween2500to5000: null,
    teuMorethan5000: null,
  },
  FollowingWind: {
    teuLessthan2500: null,
    teuBetween2500to5000: null,
    teuMorethan5000: null,
  },
  BeamWavesHeight: {
    teuLessthan2500: null,
    teuBetween2500to5000: null,
    teuMorethan5000: null,
  },
  BeamWavesPeriod: {
    teuLessthan2500: { max: null, min: null },
    teuBetween2500to5000: { max: null, min: null },
    teuMorethan5000: { max: null, min: null },
  },
  HeadWavesHeight: {
    teuLessthan2500: null,
    teuBetween2500to5000: null,
    teuMorethan5000: null,
  },
  HeadWavesPeriod: {
    teuLessthan2500: { max: null, min: null },
    teuBetween2500to5000: { max: null, min: null },
    teuMorethan5000: { max: null, min: null },
  },
  FollowingWavesHeight: {
    teuLessthan2500: null,
    teuBetween2500to5000: null,
    teuMorethan5000: null,
  },
  FollowingWavesPeriod: {
    teuLessthan2500: { max: null, min: null },
    teuBetween2500to5000: { max: null, min: null },
    teuMorethan5000: { max: null, min: null },
  },
  IceConcentration: {
    teuLessthan2500: null,
    teuBetween2500to5000: null,
    teuMorethan5000: null,
  },
};
export const CUSTOM_WEATHER_LIMIT_DATA = {
  Waves: null,
  Wind10M: null,
  Wind50M: null,
  Visibility: null,
  Icing: null,
  Hurricanes: null,
  TropicalStorms: null,
  Depressions: null,
  WindGusts10M: null,
  WindGusts50M: null,
  RiskWindSpeed: null,
  RiskWaveHeight: null,
  HeadWind: null,
  BeamWind: null,
  FollowingWind: null,
  BeamWavesHeight: null,
  BeamWavesPeriod: null,
  HeadWavesHeight: null,
  HeadWavesPeriod: { max: null, min: null },
  FollowingWavesHeight: null,
  FollowingWavesPeriod: { max: null, min: null } ,
  IceConcentration: null,
};

export const keyMapping: { [key: string]: string } = {
  teuLessthan2500: "TEU < 2500",
  teuBetween2500to5000: "TEU [2500; 5000]",
  teuMorethan5000: "TEU > 5000",
};

export function getVisibilityValueTexts() {
  return [
    { value: 0, text: "Good" },
    { value: 1, text: "Moderate/Good" },
    { value: 2, text: "Moderate" },
    { value: 3, text: "Moderate/Poor" },
    { value: 4, text: "Poor" }
  ];
}

export function getIcingValueTexts() {
  return [
    { value: 0, text: "None" },
    { value: 1, text: "Light" },
    { value: 2, text: "Moderate" },
    { value: 3, text: "Severe" },
    { value: 4, text: "Very severe" }
  ];
}